import moment, {type Moment} from 'moment';
import React, {useMemo} from 'react';

import MomentDatePicker from '../../../components/DatePicker/MomentDatePicker';

import {
  getStudyFinancialYearStartYear,
  isStudyDateReady,
  normalizeReferenceYearValue
} from './referenceYearUtils';

const {RangePicker} = MomentDatePicker;

interface StudyReferenceYearInputProps {
  value?: string[];
  onChange?: (value: string[] | undefined) => void;
  className?: string;
  studyDate?: string;
}

const StudyReferenceYearInput: React.FC<StudyReferenceYearInputProps> = ({
  value,
  onChange,
  className,
  studyDate
}) => {
  const isDisabled = !isStudyDateReady(studyDate);
  const minYear = getStudyFinancialYearStartYear(studyDate);
  const currentYear = moment().year();

  const momentValue = useMemo((): [Moment | null, Moment | null] | null => {
    const normalized = normalizeReferenceYearValue(value);
    const start = normalized?.[0];
    const end = normalized?.[1];

    if (!start || !end) {
      return null;
    }

    const startMoment = moment(start, 'YYYY', true).startOf('year');
    const endMoment = moment(end, 'YYYY', true).startOf('year');

    if (!startMoment.isValid() || !endMoment.isValid()) {
      return null;
    }

    return [startMoment, endMoment];
  }, [value]);

  const handleChange = (
    dates: [Moment | null, Moment | null] | null
  ): void => {
    if (dates == null) {
      onChange?.(undefined);

      return;
    }

    if (!dates[0]?.isValid() || !dates[1]?.isValid()) {
      return;
    }

    onChange?.([dates[0].format('YYYY'), dates[1].format('YYYY')]);
  };

  return (
    <RangePicker
      picker="year"
      format="YYYY"
      value={momentValue}
      onChange={handleChange}
      disabled={isDisabled}
      disabledDate={(current) => {
        if (current == null) {
          return false;
        }

        if (minYear != null && current.year() < minYear) {
          return true;
        }

        return current.year() > currentYear;
      }}
      placeholder={['Start year', 'End year']}
      inputReadOnly
      style={{width: '100%'}}
      className={className}
    />
  );
};

export default StudyReferenceYearInput;
